import { hasOptionPoints, orderByTriggers, type TriggerShape } from "@repo/domain";
import { createStore } from "@xstate/store";
import { conditionTargets, defaultCondition, referenceKey, temporaryOrPersisted } from "./condition-references";
import type {
  BuilderItemType,
  DraftAnswerOption,
  DraftEnableWhen,
  DraftItem,
  DraftNode,
  DraftSection,
  QuestionnaireDraft,
} from "./types";
import { createDefaultItem } from "./utils/defaults";
import { generateLinkId } from "./utils/link-id";
import {
  getItemAtPath,
  insertItemAtPath,
  moveItemInList,
  removeItemAtPath,
  updateItemAtPath,
} from "./utils/path-helpers";

const emptyDraft = (initial?: Partial<QuestionnaireDraft>): QuestionnaireDraft => ({
  ...initial,
  sections: initial?.sections ?? [],
  scores: initial?.scores ?? [],
  totalBands: initial?.totalBands ?? [],
});
interface BuilderContext {
  draft: QuestionnaireDraft;
  selectedItemPath: number[] | null;
  isDirty: boolean;
  scoringEnabled: boolean;
}
function duplicate(node: DraftNode): DraftNode {
  const copy = {
    ...node,
    id: undefined,
    clientId: generateLinkId(),
    ...("items" in node ? { items: node.items.map(duplicate) } : {}),
  } as DraftNode;
  if (copy.kind === "Question" && copy.value.kind === "Choice") {
    copy.value = {
      ...copy.value,
      options: copy.value.options.map((option) => ({ ...option, id: undefined, clientId: generateLinkId() })),
    };
  }
  return copy;
}
export function createQuestionnaireBuilderStore(initial?: Partial<QuestionnaireDraft>, scoringRequired = false) {
  const requireTask = (node: DraftNode) =>
    scoringRequired && node.kind === "Question" ? { ...node, required: true } : requireScored(node);
  return createStore({
    context: {
      draft: emptyDraft(initial),
      selectedItemPath: null as number[] | null,
      isDirty: false,
      scoringEnabled: scoringRequired || Boolean(initial?.scores?.length) || hasPoints(initial?.sections ?? []),
    },
    on: settleStructure({
      "scoring.set": (ctx, e: { enabled: boolean }) => ({
        ...ctx,
        scoringEnabled: e.enabled,
        draft: e.enabled
          ? ctx.draft
          : { ...ctx.draft, scores: [], totalBands: [], sections: ctx.draft.sections.map(clearPoints) },
        isDirty: ctx.isDirty || (!e.enabled && (ctx.draft.scores.length > 0 || hasPoints(ctx.draft.sections))),
      }),
      "scores.set": (ctx, e: { scores: QuestionnaireDraft["scores"] }) => ({
        ...ctx,
        draft: { ...ctx.draft, scores: e.scores },
        isDirty: true,
      }),
      "totalBands.set": (ctx, e: { bands: NonNullable<QuestionnaireDraft["totalBands"]> }) => ({
        ...ctx,
        draft: { ...ctx.draft, totalBands: e.bands },
        isDirty: true,
      }),
      "item.add": (ctx, e: { parentPath: number[]; itemType: BuilderItemType }) => {
        const sections = insertItemAtPath(
          ctx.draft.sections,
          e.parentPath,
          requireTask(createDefaultItem(e.itemType) as DraftNode),
        ) as QuestionnaireDraft["sections"];
        const siblings = e.parentPath.length ? getItemAtPath(sections, e.parentPath) : undefined;
        const index = (siblings && "items" in siblings ? siblings.items : sections).length - 1;
        return {
          ...ctx,
          draft: { ...ctx.draft, sections },
          selectedItemPath: [...e.parentPath, index],
          isDirty: true,
        };
      },
      "item.remove": (ctx, e: { path: number[] }) => ({
        ...ctx,
        draft: {
          ...ctx.draft,
          sections: removeItemAtPath(ctx.draft.sections, e.path) as QuestionnaireDraft["sections"],
        },
        selectedItemPath: null,
        isDirty: true,
      }),
      "item.move": (ctx, e: { fromPath: number[]; toIndex: number }) => {
        const parentPath = e.fromPath.slice(0, -1);
        const fromIndex = e.fromPath.at(-1) ?? 0;
        if (parentPath.length === 0)
          return {
            ...ctx,
            draft: {
              ...ctx.draft,
              sections: moveItemInList(ctx.draft.sections, fromIndex, e.toIndex) as QuestionnaireDraft["sections"],
            },
            isDirty: true,
          };
        const parent = getItemAtPath(ctx.draft.sections, parentPath);
        if (!parent || !("items" in parent)) return ctx;
        return {
          ...ctx,
          draft: {
            ...ctx.draft,
            sections: updateItemAtPath(ctx.draft.sections, parentPath, {
              items: moveItemInList(parent.items, fromIndex, e.toIndex),
            } as Partial<DraftNode>) as QuestionnaireDraft["sections"],
          },
          isDirty: true,
        };
      },
      "item.update": (ctx, e: { path: number[]; updates: Partial<DraftNode> }) => ({
        ...ctx,
        draft: {
          ...ctx.draft,
          sections: updateItemAtPath(
            ctx.draft.sections,
            e.path,
            requireTask({ ...getItemAtPath(ctx.draft.sections, e.path), ...e.updates } as DraftNode),
          ) as QuestionnaireDraft["sections"],
        },
        isDirty: true,
      }),
      "item.select": (ctx, e: { path: number[] | null }) => ({ ...ctx, selectedItemPath: e.path }),
      "item.duplicate": (ctx, e: { path: number[] }) => {
        const original = getItemAtPath(ctx.draft.sections, e.path);
        if (!original) return ctx;
        const parentPath = e.path.slice(0, -1);
        const parent = parentPath.length ? getItemAtPath(ctx.draft.sections, parentPath) : undefined;
        const siblings = parent && "items" in parent ? parent.items : ctx.draft.sections;
        const copy = [...siblings];
        copy.splice((e.path.at(-1) ?? 0) + 1, 0, duplicate(original));
        const sections = parentPath.length
          ? updateItemAtPath(ctx.draft.sections, parentPath, { items: copy } as Partial<DraftNode>)
          : copy;
        return { ...ctx, draft: { ...ctx.draft, sections: sections as QuestionnaireDraft["sections"] }, isDirty: true };
      },
      "option.add": (ctx, e: { path: number[] }) =>
        updateChoice(ctx, e.path, (options) => [...options, { clientId: generateLinkId(), label: "" }]),
      "option.remove": (ctx, e: { path: number[]; index: number }) =>
        updateChoice(ctx, e.path, (options) => options.filter((_, index) => index !== e.index)),
      "option.update": (ctx, e: { path: number[]; index: number; option: DraftAnswerOption }) =>
        updateChoice(ctx, e.path, (options) => options.map((option, index) => (index === e.index ? e.option : option))),
      "enableWhen.add": (ctx, e: { path: number[] }) => {
        const holder = getItemAtPath(ctx.draft.sections, e.path);
        const holderKey = holder ? referenceKey(temporaryOrPersisted(holder)) : undefined;
        const target = conditionTargets(ctx.draft.sections).find(
          (candidate) => referenceKey(candidate.reference) !== holderKey,
        );
        return target ? updateConditions(ctx, e.path, (conditions) => [...conditions, defaultCondition(target)]) : ctx;
      },
      "enableWhen.remove": (ctx, e: { path: number[]; index: number }) =>
        updateConditions(ctx, e.path, (conditions) => conditions.filter((_, index) => index !== e.index)),
      "enableWhen.update": (ctx, e: { path: number[]; index: number; condition: DraftEnableWhen }) =>
        updateConditions(ctx, e.path, (conditions) =>
          conditions.map((condition, index) => (index === e.index ? e.condition : condition)),
        ),
      "questionnaire.saved": (ctx) => ({ ...ctx, isDirty: false }),
    }),
  });
}

// Edits that can change what depends on what, or where a member sits.
const STRUCTURAL = new Set([
  "item.add",
  "item.remove",
  "item.move",
  "item.update",
  "item.duplicate",
  "enableWhen.add",
  "enableWhen.remove",
  "enableWhen.update",
]);

/** Runs every structural edit through `settle`, so no edit can leave a follow-up away from its trigger. */
function settleStructure<H extends Record<string, (ctx: BuilderContext, event: never) => BuilderContext>>(
  handlers: H,
): H {
  return Object.fromEntries(
    Object.entries(handlers).map(([type, handler]) => [
      type,
      STRUCTURAL.has(type) ? (ctx: BuilderContext, event: never) => settle(handler(ctx, event)) : handler,
    ]),
  ) as H;
}

const DRAFT_TRIGGERS: TriggerShape<DraftSection, DraftItem> = {
  items: (section) => section.items,
  withItems: (section, items) => ({ ...section, items }),
  answers: (item) =>
    item.kind === "Question"
      ? [referenceKey(temporaryOrPersisted(item))]
      : item.kind === "RepeatingGroup"
        ? item.items.map((child) => referenceKey(temporaryOrPersisted(child)))
        : [],
  triggers: (node) =>
    "conditions" in node
      ? (node.conditions ?? []).flatMap((condition) => ("fact" in condition ? [] : [referenceKey(condition.question)]))
      : [],
};

/**
 * Follow-ups placed right after what they depend on, the order the domain
 * requires. The selection is re-found by identity: a path would now point at
 * whichever member took the moved one's place.
 */
function settle(ctx: BuilderContext): BuilderContext {
  const selected = ctx.selectedItemPath ? getItemAtPath(ctx.draft.sections, ctx.selectedItemPath)?.clientId : undefined;
  const sections = orderByTriggers(ctx.draft.sections, DRAFT_TRIGGERS);
  return {
    ...ctx,
    draft: { ...ctx.draft, sections },
    selectedItemPath: selected ? pathOf(sections, selected) : ctx.selectedItemPath,
  };
}

function pathOf(nodes: readonly DraftNode[], clientId: string, at: number[] = []): number[] | null {
  for (const [index, node] of nodes.entries()) {
    if (node.clientId === clientId) return [...at, index];
    if ("items" in node) {
      const found = pathOf(node.items, clientId, [...at, index]);
      if (found) return found;
    }
  }
  return null;
}

function updateChoice(
  ctx: BuilderContext,
  path: number[],
  change: (options: DraftAnswerOption[]) => DraftAnswerOption[],
): BuilderContext {
  const item = getItemAtPath(ctx.draft.sections, path);
  if (!item || item.kind !== "Question" || item.value.kind !== "Choice") return ctx;
  const options = change([...item.value.options]);
  return {
    ...ctx,
    draft: {
      ...ctx.draft,
      sections: updateItemAtPath(
        ctx.draft.sections,
        path,
        requireScored({
          ...item,
          value: { ...item.value, options },
        }),
      ) as QuestionnaireDraft["sections"],
    },
    isDirty: true,
  };
}
function updateConditions(
  ctx: BuilderContext,
  path: number[],
  change: (conditions: DraftEnableWhen[]) => DraftEnableWhen[],
): BuilderContext {
  const item = getItemAtPath(ctx.draft.sections, path);
  if (!item || !(item.kind === "Question" || item.kind === "Section")) return ctx;
  return {
    ...ctx,
    draft: {
      ...ctx.draft,
      sections: updateItemAtPath(ctx.draft.sections, path, {
        conditions: change([...(item.conditions ?? [])]),
      } as Partial<DraftNode>) as QuestionnaireDraft["sections"],
    },
    isDirty: true,
  };
}
export type QuestionnaireBuilderStore = ReturnType<typeof createQuestionnaireBuilderStore>;

function hasPoints(nodes: DraftNode[]): boolean {
  return nodes.some((node) =>
    "items" in node
      ? hasPoints(node.items)
      : node.kind === "Question" &&
        node.value.kind === "Choice" &&
        node.value.options.some((option) => option.points !== undefined),
  );
}

function clearPoints<T extends DraftNode>(node: T): T {
  if ("items" in node) return { ...node, items: node.items.map(clearPoints) };
  if (node.kind !== "Question" || node.value.kind !== "Choice") return node;
  return {
    ...node,
    value: { ...node.value, options: node.value.options.map(({ points: _points, ...option }) => option) },
  };
}

function requireScored(node: DraftNode): DraftNode {
  return hasOptionPoints(node) ? ({ ...node, required: true } as DraftNode) : node;
}
